export const MOSCOW_TIME_ZONE = "Europe/Moscow";

export const MOSCOW_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: MOSCOW_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export const MONTH_LONG_FORMATTER = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  month: "long",
});

export const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  weekday: "short",
});

export const WEEKDAY_FORMATTER_LONG = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  weekday: "long",
});

export const DAY_OF_MONTH_FORMATTER = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  day: "numeric",
});

export const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  month: "short",
  day: "numeric",
});

export const MONTH_DAY_YEAR_FORMATTER = new Intl.DateTimeFormat("ru-RU", {
  timeZone: MOSCOW_TIME_ZONE,
  month: "short",
  day: "numeric",
  year: "numeric",
});

const getDateTimePart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) => {
  return Number(parts.find((part) => part.type === type)?.value);
};

export const getMoscowDateTimeParts = (date: Date) => {
  const parts = MOSCOW_DATE_TIME_FORMATTER.formatToParts(date);

  return {
    year: getDateTimePart(parts, "year"),
    month: getDateTimePart(parts, "month"),
    day: getDateTimePart(parts, "day"),
    hour: getDateTimePart(parts, "hour"),
    minute: getDateTimePart(parts, "minute"),
    second: getDateTimePart(parts, "second"),
  };
};

const createMoscowDate = (
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
) => new Date(Date.UTC(year, month - 1, day, hour, minute, second));

export const fromCalendarDate = (date: Date) =>
  createMoscowDate(date.getFullYear(), date.getMonth() + 1, date.getDate());

export const toCalendarDate = (date: Date) => {
  const {year, month, day} = getMoscowDateTimeParts(date);

  return new Date(year, month - 1, day);
};

export const formatMoscowDate = (date: Date) => {
  const {year, month, day} = getMoscowDateTimeParts(date);

  return [year, String(month).padStart(2, "0"), String(day).padStart(2, "0")].join("-");
};

export const startOfDay = (date: Date) => {
  const {year, month, day} = getMoscowDateTimeParts(date);

  return createMoscowDate(year, month, day);
};

export const isSameDay = (left: Date, right: Date) =>
  startOfDay(left).getTime() === startOfDay(right).getTime();

export const isBefore = (date: Date, compareTo: Date) => date.getTime() < compareTo.getTime();

export const addDays = (date: Date, days: number) => {
  const nextDate = new Date(startOfDay(date));
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate;
};

export const startOfWeek = (date: Date) => {
  const normalizedDate = startOfDay(date);
  const day = normalizedDate.getUTCDay();
  const offset = day === 0 ? 6 : day - 1;

  return addDays(normalizedDate, -offset);
};

export const isDateInWeek = (date: Date, weekStart: Date) => {
  const normalizedDate = startOfDay(date);
  const weekEnd = addDays(weekStart, 6);

  return normalizedDate >= weekStart && normalizedDate <= weekEnd;
};

export const formatWeekLabel = (weekStart: Date) => {
  const weekEnd = addDays(weekStart, 6);
  const weekStartParts = getMoscowDateTimeParts(weekStart);
  const weekEndParts = getMoscowDateTimeParts(weekEnd);
  const isSameMonth = weekStartParts.month === weekEndParts.month;
  const isSameYear = weekStartParts.year === weekEndParts.year;

  let label = "";

  if (isSameMonth && isSameYear) {
    label = `${MONTH_LONG_FORMATTER.format(weekStart)} ${weekStartParts.year}`;
  } else if (isSameYear) {
    label = `${MONTH_LONG_FORMATTER.format(weekStart)} - ${MONTH_LONG_FORMATTER.format(weekEnd)} ${weekStartParts.year}`;
  } else {
    label = `${MONTH_LONG_FORMATTER.format(weekStart)} ${weekStartParts.year} - ${MONTH_LONG_FORMATTER.format(weekEnd)} ${weekEndParts.year}`;
  }

  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getWeekDays = (weekStart: Date) =>
  Array.from({length: 7}, (_, index) => addDays(weekStart, index));

export const getMinBookingDate = () => startOfDay(new Date());
export const getMaxBookingDate = () => addDays(getMinBookingDate(), 30);
export const getMaxViewDate = () => addDays(getMinBookingDate(), 60);
